import type {
  AccountEntity,
  DealerCode,
  DealerCodeFromADD
} from "@dealer-center/ddc-api-account-interface";
import type { TypeSafeTableColumns } from "@dealer-center/core-components";
import {
  Alert,
  Button,
  PencilSquareIcon,
  Table,
  TrashIcon
} from "@dealer-center/core-components";
import styled from "styled-components";
import { TagManager } from "@dealer-center/core-third-party-gtm";
import { useDealerCodesByAccountIdGet } from "@dealer-center/ddc-pages-dealer-codes-fetch";
import { useDealerCodeModals } from "../modals";
import { useCC9Flags } from "@dealer-center/core-third-party-launch-darkly";

const StyledTable = styled(Table.Table)`
  width: 100%;
`;

interface DealerCodeTableProps {
  accountEntity: AccountEntity;
}

export const DealerCodeTable = ({ accountEntity }: DealerCodeTableProps) => {
  const { "css.enable_dealer_code_service_use": useCssForDealerCodes } =
    useCC9Flags();
  const {
    data: dealerCodesFromCss,
    isLoading,
    error
  } = useDealerCodesByAccountIdGet(
    useCssForDealerCodes ? accountEntity.id : undefined
  );
  const dealerCodeFromAdd = !useCssForDealerCodes
    ? accountEntity.dealerCodes
    : [];

  const dealerCodes = useCssForDealerCodes
    ? dealerCodesFromCss
    : dealerCodeFromAdd;
  const dealerCodeModals = useDealerCodeModals();
  if (error) {
    return <Alert type="error">There was an error loading dealer codes</Alert>;
  }

  const columns: TypeSafeTableColumns<DealerCodeFromADD> = [
    {
      title: "Type",
      dataIndex: "type"
    },
    {
      title: "Code",
      dataIndex: "code"
    },
    {
      title: "Manage Dealer Code",
      className: "column-extraData",
      columnSortable: false,
      render: (_text: string, dealerCode: DealerCode) => (
        <Button
          analyticsConfiguration={{
            eventName: "Dealer Code Manage"
          }}
          buttonStyle="aux"
          data-testid={`manage-dealer-code-${dealerCode.code}-${dealerCode.id}`}
          onClick={() => {
            TagManager.event({
              eventName: "Dealer Code Manage",
              eventProperties: {
                component: "dashboard",
                actionValue: dealerCode.code
              }
            });
            window.open(
              `${window.location.origin}/landing/dealer-codes/${dealerCode.type}`,
              "_blank"
            );
          }}
        >
          Manage
        </Button>
      )
    },
    {
      title: "Edit",
      className: "column-extraData",
      columnSortable: false,
      render: (_text: string, dealerCode: DealerCode) => (
        <Button
          analyticsConfiguration={{
            eventName: "Dealer Code Edit"
          }}
          buttonStyle="aux"
          data-testid={`edit-dealer-code-${dealerCode.code}-${dealerCode.type}`}
          onClick={() => {
            TagManager.event({
              eventName: "Dealer Code Edit",
              eventProperties: {
                component: "dashboard",
                actionValue: dealerCode.code
              }
            });
            dealerCodeModals.showEditModal({
              id: dealerCode.id,
              dealerCodeType: dealerCode.type,
              dealerCode: dealerCode.code,
              account: {
                id: accountEntity.id,
                displayName: accountEntity.name
              }
            });
          }}
        >
          <PencilSquareIcon title="Edit" />
        </Button>
      )
    },
    {
      title: "Delete",
      className: "column-extraData",
      columnSortable: false,
      render: (_text: string, dealerCode: DealerCode) => (
        <Button
          analyticsConfiguration={{
            eventName: "Dealer Code Delete"
          }}
          buttonStyle="aux"
          data-testid={`delete-dealer-code-${dealerCode.code}-${dealerCode.type}`}
          onClick={() => {
            TagManager.event({
              eventName: "Dealer Code Delete",
              eventProperties: {
                component: "dashboard",
                actionValue: dealerCode.code
              }
            });
            dealerCodeModals.showDeleteModal({
              id: dealerCode.id,
              dealerCodeType: dealerCode.type,
              account: {
                id: accountEntity.id,
                displayName: accountEntity.name
              }
            });
          }}
        >
          <TrashIcon title="Delete" />
        </Button>
      )
    }
  ];

  return (
    <>
      <StyledTable
        displayFilter
        displayPageSizeSelector
        sortableColumns
        columns={columns}
        data={dealerCodes || []}
        defaultPageSize={20}
        emptyText="No dealer codes found"
        isLoading={isLoading}
      />
      <Button
        analyticsConfiguration={{
          eventName: "Add Dealer Code"
        }}
        sx={{ float: "right" }}
        onClick={() => {
          dealerCodeModals.showAddDealerToCodeModal(accountEntity.id);
        }}
      >
        Add
      </Button>
      {dealerCodeModals.modal}
    </>
  );
};
